import React, { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import { Empty, Segmented, Space, Tag, Typography } from 'antd';

const { Text } = Typography;

// 自动降采样阈值：周点数超过 WEEK_MAX 退到月，月点数超过 MONTH_MAX 退到季度。
const WEEK_MAX = 40;
const MONTH_MAX = 36;

// 机场线条配色（循环取用，超出则回绕）。
const PALETTE = [
    '#1890ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1',
    '#13c2c2', '#fadb14', '#f5222d', '#2f54eb', '#a0d911',
    '#fa541c', '#1677ff', '#08979c', '#d4380d', '#9254de',
    '#c41d7f', '#7cb305', '#0958d9', '#d48806', '#531dab',
];

const weekSeq = (label) => {
    const m = /W(\d+)/i.exec(label || '');
    return m ? Number(m[1]) : 99;
};

// 把一条长表记录映射到指定粒度的时间桶：返回 { key, label, sort }。
const bucketOf = (item, gran) => {
    const ym = (item.period_date || '').slice(0, 7); // YYYY-MM
    if (gran === 'month') {
        return { key: ym, label: ym, sort: ym };
    }
    if (gran === 'quarter') {
        const [y, m] = ym.split('-');
        const q = Math.floor((Number(m) - 1) / 3) + 1;
        return { key: `${y}-Q${q}`, label: `${y} Q${q}`, sort: `${y}-${q}` };
    }
    // week：每个 (报价期, 周次) 为独立点
    return {
        key: `${item.period_date}__${item.week_label}`,
        label: item.week_label,
        sort: `${item.period_date}-${String(weekSeq(item.week_label)).padStart(3, '0')}`,
    };
};

/**
 * 各地机场油价趋势折线图
 * - 纵轴：价格；横轴：week（按时间从旧到新）
 * - 每个机场一条线
 * - 点过多时自动降采样：周 → 月 → 季度（聚合取该桶各周均值）
 */
const AirportFuelPriceTrendChart = ({
    items = [],
    airports = [],
    unit = 'USD/T',
    granularityMode = 'auto',
    onGranularityModeChange,
    height = 320,
}) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    const model = useMemo(() => {
        const weekPoints = new Set(items.map((it) => `${it.period_date}__${it.week_label}`)).size;
        const monthPoints = new Set(items.map((it) => (it.period_date || '').slice(0, 7))).size;

        let effectiveGran = granularityMode;
        if (granularityMode === 'auto') {
            if (weekPoints <= WEEK_MAX) effectiveGran = 'week';
            else if (monthPoints <= MONTH_MAX) effectiveGran = 'month';
            else effectiveGran = 'quarter';
        }

        // bucketKey -> { label, sort, airport -> {sum, count} }
        const buckets = new Map();
        items.forEach((it) => {
            const low = Number(it.price_usd_per_t);
            if (!Number.isFinite(low)) return;
            // 区间价取中值参与绘制与聚合；单值价用本身
            const highRaw = it.price_usd_per_t_high;
            const high = highRaw === null || highRaw === undefined ? null : Number(highRaw);
            const price = high !== null && Number.isFinite(high) ? (low + high) / 2 : low;
            const b = bucketOf(it, effectiveGran);
            if (!buckets.has(b.key)) {
                buckets.set(b.key, { label: b.label, sort: b.sort, byAirport: new Map() });
            }
            const bucket = buckets.get(b.key);
            const agg = bucket.byAirport.get(it.airport_code) || { sum: 0, count: 0 };
            agg.sum += price;
            agg.count += 1;
            bucket.byAirport.set(it.airport_code, agg);
        });

        const orderedKeys = Array.from(buckets.keys()).sort((a, b) =>
            buckets.get(a).sort.localeCompare(buckets.get(b).sort),
        );
        const categories = orderedKeys.map((k) => buckets.get(k).label);

        // 仅渲染在当前数据中至少出现一次的机场，保持图例整洁。
        const activeAirports = airports.filter((code) =>
            orderedKeys.some((k) => buckets.get(k).byAirport.has(code)),
        );

        const series = activeAirports.map((code, idx) => ({
            name: code,
            type: 'line',
            connectNulls: true,
            showSymbol: categories.length <= 60,
            symbolSize: 4,
            smooth: false,
            lineStyle: { width: 1.5 },
            itemStyle: { color: PALETTE[idx % PALETTE.length] },
            data: orderedKeys.map((k) => {
                const agg = buckets.get(k).byAirport.get(code);
                if (!agg) return null;
                return Math.round((agg.sum / agg.count) * 100) / 100;
            }),
        }));

        return { effectiveGran, weekPoints, monthPoints, categories, series, hasData: series.length > 0 };
    }, [items, airports, granularityMode]);

    useEffect(() => {
        if (!chartRef.current || !model.hasData) return;
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                textStyle: { fontSize: 11 },
                order: 'valueDesc',
            },
            legend: {
                type: 'scroll',
                top: 0,
                data: model.series.map((s) => s.name),
                textStyle: { fontSize: 11 },
            },
            grid: { left: '2%', right: '3%', bottom: 56, top: 40, containLabel: true },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: model.categories,
                axisLabel: { rotate: 45, fontSize: 9, color: '#8c8c8c', hideOverlap: true },
                axisLine: { lineStyle: { color: '#d9d9d9' } },
                axisTick: { show: false },
            },
            yAxis: {
                type: 'value',
                scale: true,
                name: unit,
                nameTextStyle: { fontSize: 10, color: '#8c8c8c' },
                axisLabel: { fontSize: 9, color: '#8c8c8c' },
                splitLine: { lineStyle: { color: '#f0f0f0' } },
            },
            dataZoom: [
                { type: 'inside', start: 0, end: 100 },
                { type: 'slider', height: 16, bottom: 16, start: 0, end: 100 },
            ],
            series: model.series,
        };

        chartInstance.current.setOption(option, true);
        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [model, unit]);

    useEffect(() => () => {
        chartInstance.current?.dispose();
        chartInstance.current = null;
    }, []);

    const granLabel = { week: '周', month: '月', quarter: '季度' }[model.effectiveGran];
    const autoHint =
        granularityMode === 'auto'
            ? `（自动：周点 ${model.weekPoints} / 月点 ${model.monthPoints} → 当前按${granLabel}）`
            : '';

    return (
        <div style={{ marginTop: 20, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                <Space size={8} wrap>
                    <Text strong style={{ fontSize: 14 }}>📈 油价趋势</Text>
                    <Tag color="blue">单位：{unit}</Tag>
                    {model.hasData && <Text type="secondary" style={{ fontSize: 12 }}>当前粒度：{granLabel}{autoHint}</Text>}
                </Space>
                <Segmented
                    size="small"
                    value={granularityMode}
                    onChange={onGranularityModeChange}
                    options={[
                        { label: '自动', value: 'auto' },
                        { label: '周', value: 'week' },
                        { label: '月', value: 'month' },
                        { label: '季度', value: 'quarter' },
                    ]}
                />
            </div>
            {model.hasData ? (
                <div ref={chartRef} style={{ width: '100%', height: `${height}px`, background: 'white' }} />
            ) : (
                <Empty description="暂无可绘制的油价数据" style={{ padding: '32px 0' }} />
            )}
        </div>
    );
};

export default AirportFuelPriceTrendChart;
